import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import { calculateDateDifference } from '../../General/Functions/datedifference';
import defaultimg from '../../../assets/defaultimg.webp';
import { getLoginId, isLoggedIn } from '../../../components/Authentication/AuthSvc/authservice';
import ContentUnavailable from '../../ContentUnavaliable/contentunavaliable';

import './projectcontent.css';

const API_URL = process.env.REACT_APP_API_URL;

const ProjectContent = () => {
  const [project, setProject] = useState(null);
  const [projRoles, setProjRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contentRestricted, setContentRestricted] = useState(false);
  const [restrictionInfo, setRestrictionInfo] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLiked, setIsLiked] = useState(false);
  const [likeId, setLikeId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { id } = useParams();

  // Handle like/unlike
  const handleLikeClick = async () => {
    if (!isLoggedIn()) {
      alert('Please log in to like projects');
      return;
    }

    const userId = getLoginId();
    if (!userId) {
      alert('Please log in to like projects');
      return;
    }

    try {
      if (isLiked) {
        await fetch(`${API_URL}/project/${id}/like/${userId}`, {
          method: 'DELETE'
        });
        setIsLiked(false);
        if (project) {
          setProject({
            ...project,
            likes: Math.max((project.likes || 0) - 1, 0)
          });
        }
      } else {
        await fetch(`${API_URL}/project/${id}/like/${userId}`, {
          method: 'POST'
        });
        setIsLiked(true);
        if (project) {
          setProject({
            ...project,
            likes: (project.likes || 0) + 1
          });
        }
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  // Handle report submission
  const handleReport = async () => {
    if (!reportReason || !reportDescription) {
      alert('Please fill in all fields');
      return;
    }
  
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          contentType: 'project',
          contentId: parseInt(id),
          category: reportReason,
          description: reportDescription
        })
      });
  
      if (!response.ok) throw new Error('Failed to submit report');
  
      alert('Report submitted successfully');
      setShowReportDialog(false);
      setReportReason('');
      setReportDescription('');
    } catch (error) {
      console.error('Error submitting report:', error);
      alert('Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fetch project data
  useEffect(() => {
    const checkIfLiked = async (userId) => {
      try {
        const response = await fetch(`${API_URL}/project/${id}/like/${userId}`);
        const data = await response.json();
        setIsLiked(data.liked);
        setLikeId(data.likeId);
      } catch (error) {
        console.error('Error checking like status:', error);
      }
    };

    const userId = getLoginId();
    setCurrentUser(userId);
    if (userId) {
      checkIfLiked(userId);
    }

    const fetchProject = async () => {
      if (!id) return;
      try {
        const response = await fetch(`${API_URL}/project/${id}`);
        
        // Check if response is not OK
        if (!response.ok) {
          // Check if the content is unavailable due to moderation
          if (response.status === 403) {
            const errorData = await response.json();
            if (errorData.error === "Content unavailable") {
              setContentRestricted(true);
              setRestrictionInfo({
                reason: errorData.reason,
                expiresAt: errorData.expires_at
              });
              setLoading(false);
              return;
            }
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        setProject(data.project_details);
        
        if (data.roles_skills?.role_name) {
          const roleNames = data.roles_skills.role_name.split(', ');
          const roleIds = data.roles_skills.role_id.split(', ');
          const descriptions = data.roles_skills.role_description?.split(', ') || 
            Array(roleNames.length).fill('No description available');
          
          const rolesArray = roleNames.map((name, index) => ({
            role_id: roleIds[index],
            role_name: name.trim(),
            description: descriptions[index]?.trim() || 'No description available'
          }));
          
          setProjRoles(rolesArray);
          setSelectedRole(rolesArray[0]);
        }
        
        setTeamMembers(data.team_members || []);
      } catch (error) {
        console.error('Error fetching project', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [id]);

  const handleRoleChange = (event) => {
    const selectedRoleName = event.target.value;
    const role = projRoles.find(role => role.role_name === selectedRoleName);
    setSelectedRole(role);
  };

  if (loading) {
    return <div className="projectbody">Loading...</div>;
  }

  if (contentRestricted) {
    return (
      <div className="projectbody">
        <ContentUnavailable 
          reason={restrictionInfo?.reason}
          expiresAt={restrictionInfo?.expiresAt}
        />
      </div>
    );
  }

  if (error) {
    return <div className="projectbody">Error: {error}</div>;
  }

  if (!project) {
    return <div className="projectbody">No project data found</div>;
  }

  const projectTags = (project.tags || '').split(',').map(tag => tag.trim()).filter(Boolean);

  return (
    <div className="projectbody">
      <div className="project-grid-container">
        <div className="project-column1">
          <div className="project-grid-item project-grid-1">
            <h2>{project.title}</h2>
            <br />

            <div className="mobile-project-image">
              <div className="project-image-container">
                <img 
                  src={project.image || defaultimg} 
                  alt="Project" 
                  className="projectImg"
                />
              </div>
            </div>

            <p className="projheadline">
              <span className={`projphasestatus ${
                project.phase === 'Onboarding' ? 'projphaseonboarding' :
                project.phase === 'Initiation' ? 'projphaseinitiation' :
                project.phase === 'Execution' ? 'projphaseexecution' :
                project.phase === 'Closed' ? 'projphaseclosed' : ''
              }`}>
                {project.phase}
              </span>
              <span>Recruiting:</span>
              <span className="projrecruiting">
                {project.recruiting ? (
                  <span> Open </span>
                ) : (
                  <span className="text-red-500"> Closed </span>
                )}
              </span>
              | Posted {calculateDateDifference(project.creation_date)}
            </p>
            <br />
            <p className="projdesc">{project.details}</p>
            <br />

            <div>
              <label htmlFor="roleSelect">Role Requirements</label>
              <select 
                id="roleSelect" 
                className="projselector" 
                onChange={handleRoleChange}
                value={selectedRole?.role_name || ''}
              >
                {projRoles.map(role => (
                  <option key={role.role_id} value={role.role_name}>
                    {role.role_name}
                  </option>
                ))}
              </select>
              
              {selectedRole && (
                <div className="mt-4">
                  <h4 className="font-medium mb-2">{selectedRole.role_name}</h4>
                  <p className="projdesc">
                    {selectedRole.description}
                  </p>
                </div>
              )}
              <br />
            </div>

            <div className="projtags">
              {projectTags.map((tag, index) => (
                <div key={index} className="projtag">
                  {tag}
                </div>
              ))}
            </div>

            <div className="projabout">
              <div className="projicons">
                <i className="fa-solid fa-user-plus projicon" />
                {project.recruiting ? (
                  <span>Open for Collaboration</span>
                ) : (
                  <span>Closed</span>
                )}
              </div>
              <div className="projicons">
                <i className="fa-regular fa-clock projicon text-green-500" />
                Active {calculateDateDifference(project.creation_date)}
              </div>
              <div className="projicons" onClick={handleLikeClick} style={{ cursor: 'pointer' }}>
                <i className={`${isLiked ? 'fa-solid' : 'fa-regular'} fa-heart projicon text-red-500`} />
                <span>{project.likes || 0} Likes</span>
              </div>
              <div className="projicons">
                <button 
                  onClick={() => setShowReportDialog(true)}
                  className="text-red-600 hover:text-red-700"
                  style={{ 
                    backgroundColor: '#DC2626',
                    color: 'white',
                    padding: '0.4rem 1rem',
                    borderRadius: '0.5rem',
                    border: 'none'
                  }}
                >
                  Report Project
                </button>
              </div>
            </div>
          </div>

          <div className="project-grid-item project-grid-2">
            <h2 className="grid-2-title">Meet the team</h2>
            <hr />
            
            {/* Project Owner */}
            <div className="teammember">
              <div className="teammember-column1">
                <div className="membersImg">
                  <img 
                    src={project.profile_pic || defaultimg} 
                    alt="Project Owner" 
                    className="membersImg2"
                  />
                </div>
              </div>
              <div className="teammember-column2">
                <div>
                  {project.first_name} {project.last_name} 
                  <span className="text-sm font-light ml-2">(Project Owner)</span>
                </div>
                <div>
                  {project.course} 
                  {project.mat_year && ` Y${project.mat_year}`}
                </div>
                <div className="teammemberdesc">{project.description}</div>
                <div>
                  <RouterLink to={`/user/${project.user_id}`} className="teambtn">
                    View &gt;
                  </RouterLink>
                </div>
              </div>
            </div>
            <hr />

            {/* Team Members */}
            {teamMembers.length > 0 && teamMembers.map((member) => (
              member.user_id !== project.user_id && (
                <React.Fragment key={member.member_id}>
                  <div className="teammember">
                    <div className="teammember-column1">
                      <div className="membersImg">
                        <img 
                          src={member.profile_pic || defaultimg} 
                          alt={`${member.first_name} ${member.last_name}`} 
                          className="membersImg2"
                        />
                      </div>
                    </div>
                    <div className="teammember-column2">
                      <div>
                        {member.first_name} {member.last_name}
                        <span className="text-sm font-light ml-2">({member.role})</span>
                      </div>
                      <div>
                        {member.course}
                        {member.mat_year && ` Y${member.mat_year}`}
                      </div>
                      <div className="teammemberdesc">{member.description}</div>
                      <div>
                        <RouterLink to={`/user/${member.user_id}`} className="teambtn">
                          View &gt;
                        </RouterLink>
                      </div>
                    </div>
                  </div>
                  <hr />
                </React.Fragment>
              )
            ))}

          </div>
        </div>

        <div className="project-column2">
          <div className="project-grid-3">
            <img 
              src={project.image || defaultimg} 
              alt="Project" 
              className="projectImg"
            />
          </div>
          <div className="project-grid-item project-grid-4">
            <h2 className="font-normal">About the Owner</h2>
            <br />
            <h3 className="font-normal">
              {project.first_name} {project.last_name}
            </h3>
            <p className="tracking-wide">
              {project.course}
              {project.mat_year && ` Y${project.mat_year}`}
            </p>
            <br />
            <p className="projdesc">{project.description}</p>
            <br />
            <p className="mb-4">We are looking for ...</p>
            <ul className="ul-roles">
              {projRoles.map(role => (
                <li key={role.role_id} className="mb-4">
                  {role.role_name}
                </li>
              ))}
            </ul>
            <br />
            <div className="projchat">
              <p>Chat with {project.first_name}</p>
              <a href={`mailto:${project.email}`} target="_blank" rel="noopener noreferrer">
                <button className="projchatbtn">Contact</button>
              </a>
            </div>
          </div>
        </div>
      </div>

      {showReportDialog && (
        <div className="modal-overlay" onClick={() => setShowReportDialog(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2 style={{ marginBottom: '1.5rem' }}>Report Project</h2>
            <div className="form-group">
              <label>Reason for Report</label>
              <select 
                className="form-select"
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
              >
                <option value="">Select a reason</option>
                <option value="inappropriate_content">Inappropriate Content</option>
                <option value="fake_information">Fake Information</option>
                <option value="spam">Spam</option>
                <option value="copyright_violation">Copyright Violation</option>
                <option value="misleading_information">Misleading Information</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                className="form-textarea"
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                placeholder="Please provide more details about the issue..."
                rows={4}
              />
            </div>
            <div className="modal-buttons">
              <button 
                className="modal-button cancel"
                onClick={() => setShowReportDialog(false)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                className="modal-button submit"
                onClick={handleReport}
                disabled={isSubmitting || !reportReason || !reportDescription}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Report'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectContent;
